import { useState } from "react";
import { Button, Checkbox, Form, Input, Select, Space } from "../../../../../../../shared/frontend/ui";
import { DetailField, DetailList } from "../../../../../../../shared/frontend/components/DetailList";
import { getPaymentMethodLabel } from "../../../../../../../shared/frontend/billing/paymentMethodDisplay";
import { useFormDirty } from "../../../../../../../shared/frontend/forms/useFormDirty";
import { FormDrawer } from "../../../components/FormDrawer";
import { useAuth } from "../../../app/auth/useAuth";
import { useI18n } from "../../../app/i18n/useI18n";
import { usePersonPaymentProfile } from "./usePersonPaymentProfile";
import { PersonPaymentProfileImpact } from "./PersonPaymentProfileImpact";

export function PersonPaymentProfileDrawer({ personId, canWrite }: { personId: string; canWrite: boolean }) {
  const auth = useAuth();
  const { text, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [form] = Form.useForm<{ localMethod?: number; providerMethod?: string; blocked: boolean; reason: string }>();
  const { initialize, dirty } = useFormDirty(form);
  const state = usePersonPaymentProfile(auth, personId, text);
  const show = async () => {
    setOpen(true); setUrl(null);
    const data = await state.load();
    if (data) initialize({ blocked: data.profile.automaticPaymentsBlocked, reason: data.profile.blockReason ?? "", localMethod: undefined, providerMethod: undefined });
  };
  const blocked = Form.useWatch("blocked", form) === true;
  const providerMethod = Form.useWatch("providerMethod", form);
  const localMethod = Form.useWatch("localMethod", form);
  const providerOption = state.data?.options.find(option => option.paymentMethod === providerMethod);
  const target = localMethod != null ? { local: localMethod } : providerOption ? { method: providerOption.paymentMethod, interfaceId: providerOption.id } : null;
  return <>
    <Button onClick={() => show()}>{text("Payment profile")}</Button>
    <FormDrawer mode="edit" open={open} title="Payment profile" onCancel={() => setOpen(false)} dirty={dirty} readOnly={!canWrite}
      formId="person-payment-profile" contentLoading={state.loading} submitting={state.saving} submitDisabled={!state.data}>
      <DetailList><DetailField label={text("Payment method")}>
        {state.data?.paymentMethodName ?? (state.data?.profile.paymentMethod ? getPaymentMethodLabel(state.data.profile.paymentMethod,
          { native_sepa: text("Direct debit"), card: text("Credit card") }) : text("Not set"))}
      </DetailField></DetailList>
      <Form id="person-payment-profile" form={form} layout="vertical" onFinish={async values => {
        if (!await state.save(values.localMethod, values.blocked, values.reason)) return;
        setOpen(false);
      }}>
        <Form.Item name="localMethod" label={text("Payment method")}>
          <Select allowClear options={state.data?.localMethods.map(method => ({ value: method.method, label: method.name }))} />
        </Form.Item>
        <Form.Item name="providerMethod" label={text("Set up online payment authorization")}>
          <Select allowClear options={state.data?.options.filter((method, index, all) => all.findIndex(other => other.paymentMethod === method.paymentMethod) === index)
            .map(method => ({ value: method.paymentMethod, label: getPaymentMethodLabel(method.paymentMethod,
              { card: text("Credit card"), sepa_debit: text("Direct debit") }) }))} />
        </Form.Item>
        <PersonPaymentProfileImpact auth={auth} personId={personId} target={target} text={text} />
        <Space direction="vertical">
          <Button disabled={!canWrite || state.saving || !providerMethod} onClick={async () => {
            const method = form.getFieldValue("providerMethod");
            const option = state.data?.options.find(option => option.paymentMethod === method);
            if (option) setUrl(await state.setup(option.id, option.paymentMethod, lang));
          }}>{text("Create authorization link")}</Button>
          {url && <Input readOnly value={url} aria-label={text("Authorization link")} />}
        </Space>
        <Form.Item name="blocked" valuePropName="checked"><Checkbox>{text("Block automatic payments")}</Checkbox></Form.Item>
        <Form.Item name="reason" label={text("Reason")} rules={[{ required: blocked, whitespace: true }]}><Input.TextArea /></Form.Item>
      </Form>
    </FormDrawer>
  </>;
}
